import { AlertV2, ButtonV2, DataGrid, StandardTabsV2 } from '@cimplico/components';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { asyncToast } from '../../../../components/toast/Toast';
import { useFirmContext } from '../../../../providers/FirmProvider';
import { useCOAs, useDownloadCOA, useEntityAccounts } from '../../../../queries/use-coa';
import { useSyncEntityChart } from '../../../../queries/use-sync-entity-chart';
import { AccountClassification, COASyncReport, Entity } from '../../../../types/entity';
import { IntegrationError } from '../../../../types/errors/integration-error';
import Account from './Account';
import ChartProviderIcon from './ChartProviderIcon';
import ManualImport from './ManualImport';

type ChartProps = {
    id: string;
    entity: Entity;
};

function Chart({ entity, id }: ChartProps): JSX.Element {
    const navigate = useNavigate();
    const { shortId } = useFirmContext();
    const chartQuery = useCOAs(entity.id, { enabled: true });
    const { mutate, isLoading } = useSyncEntityChart(entity.id);
    const [tab, setTab] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);
    const [integrationError, setIntegrationError] = useState<IntegrationError>();
    const [error, setError] = useState<string>();
    const [results, setResults] = useState<COASyncReport>();
    const [replacing, setReplacing] = useState(false);
    const downloadQuery = useDownloadCOA(entity.id, id);

    const accountsQuery = useEntityAccounts(entity.id, id, {
        filter: {
            class: Object.values(AccountClassification)[tab - 1],
        },
        sort: {
            dir: 'asc',
            field: 'code',
        },
        page,
        limit,
    });

    const chart = chartQuery.data?.charts?.find((c) => c.id === id);
    const accounts = accountsQuery.data?.items ?? [];

    const isManualChart = chart?.provider === null;

    useEffect(() => {
        if (chart?.provider === undefined) {
            return;
        }
        if (!isManualChart) {
            sync();
        }
    }, [isManualChart, mutate]);

    function clearBanners(): void {
        setIntegrationError(undefined);
        setError(undefined);
        setResults(undefined);
    }

    function download(): void {
        downloadQuery.mutate(undefined);
    }

    function sync(): void {
        clearBanners();

        const done = asyncToast('Re-syncing...');
        mutate(
            { chartId: id },
            {
                onSuccess: (res) => {
                    setResults(res.results);
                    done('success', { title: 'Successfully re-synced chart of accounts' });
                },
                onError: (err) => {
                    const error = err.response?.data;
                    if (!error) {
                        setError('Something went wrong.');
                    } else if ('message' in error) {
                        setError(error.message);
                    } else {
                        setIntegrationError(error);
                    }

                    done('error', { title: 'Unable to re-sync chart of accounts!' });
                },
            }
        );
    }

    return (
        <div className="h-full flex flex-col pb-8 relative">
            <div className="mb-4 flex items-center gap-2">
                <ChartProviderIcon provider={chart?.provider} />
                <div className="flex-1"></div>
                {accounts.length > 0 && (
                    <ButtonV2
                        size="sm"
                        type="secondary"
                        label="Download"
                        leftIcon={<ArrowDownTrayIcon />}
                        onClick={download}
                        isDisabled={downloadQuery.isLoading}
                    />
                )}
                {isManualChart && accounts.length > 0 && !replacing && (
                    <ButtonV2
                        size="sm"
                        type="secondary"
                        label="Replace"
                        leftIcon={<ArrowPathIcon />}
                        onClick={(): void => setReplacing(true)}
                    />
                )}
                {replacing && <ButtonV2 type="secondary" size="sm" label="Cancel" onClick={(): void => setReplacing(false)} />}
                {!isManualChart && (
                    <ButtonV2
                        type="secondary"
                        size="sm"
                        label={isLoading ? 'Re-syncing...' : 'Re-sync'}
                        leftIcon={<ArrowPathIcon className={`${isLoading ? 'animate-spin' : ''}`} />}
                        isDisabled={isLoading}
                        onClick={sync}
                    />
                )}
            </div>

            {integrationError && (
                <AlertV2
                    title={integrationError.error}
                    description={integrationError.description}
                    onClose={(): void => setError(undefined)}
                    type="warn"
                    className="mb-2"
                >
                    <AlertV2.Action
                        label="Reconnect to Xero"
                        onClick={(): void => navigate(`/${shortId}/entities/${entity.id}?tab=integrations&focusedIntegration=xero`)}
                    />
                    <AlertV2.ExternalLink
                        href={'https://support.cimplico.com/en/collections/7176340-workpapers-integrations'}
                        label="Learn more"
                    />
                </AlertV2>
            )}
            {error && <AlertV2 title={error} type="error" onClose={(): void => setError(undefined)} className="mb-2" />}
            {results && (
                <AlertV2
                    title="Successfully updated the chart of accounts."
                    type="success"
                    onClose={(): void => setResults(undefined)}
                    className="mb-2"
                    description={`${results.createdCount} accounts created, ${results.updatedCount} updated, ${results.deletedCount} deleted.`}
                />
            )}

            {(isManualChart && accounts.length === 0 && !accountsQuery.isLoading) || replacing ? (
                <ManualImport
                    isReplacing={replacing}
                    chartId={id}
                    entityId={entity.id}
                    onImport={(report): void => {
                        setResults(report);
                        setReplacing(false);
                    }}
                />
            ) : (
                <>
                    {/* Tabs */}
                    <div className="border-b">
                        <StandardTabsV2 onChange={setTab} name="accountClass" pill selectedIndex={tab}>
                            <StandardTabsV2.Tab label="All" />
                            <StandardTabsV2.Tab label="Assets" />
                            <StandardTabsV2.Tab label="Liabilities" />
                            <StandardTabsV2.Tab label="Equity" />
                            <StandardTabsV2.Tab label="Revenue" />
                            <StandardTabsV2.Tab label="Expenses" />
                        </StandardTabsV2>
                    </div>
                    <DataGrid
                        isSelectable={false}
                        totalRows={accountsQuery.data?.count ?? 0}
                        onPageChange={setPage}
                        onRowSizeChange={setLimit}
                        columns={[
                            { header: 'Code', alignment: 'left', width: '0.5fr' },
                            { header: 'Name', alignment: 'left', width: '2.5fr' },
                            { header: 'Type', alignment: 'left' },
                            { header: 'Report Code', alignment: 'left' },
                            { header: 'Can Journal', alignment: 'center' },
                            { header: 'Is Tax', alignment: 'center' },
                        ]}
                        rowSizes={[100, 200, 500]}
                        isLoading={accountsQuery.isLoading || isLoading}
                    >
                        {accounts.map((account) => (
                            <Account account={account} key={account.id} />
                        ))}
                    </DataGrid>
                </>
            )}
        </div>
    );
}

export default Chart;
